import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

/**
 * Yeniden kullanılabilir modül erişim yönetimi ekranı.
 * api: { getModuleAccessUsers(), updateModuleAccessUsers(ids, emailIds?) }
 * accent: tailwind renk anahtarı ('rose' | 'orange' | ...)
 * emailToggle: true ise kullanıcı bazında hatırlatma maili seçilir
 */
const ACCENTS = {
  rose: { text: 'text-rose-600', bg: 'bg-rose-600 hover:bg-rose-700', soft: 'bg-rose-50 dark:bg-rose-950/30', ring: 'focus:ring-rose-500 text-rose-600', chipsel: 'bg-rose-500' },
  orange: { text: 'text-orange-600', bg: 'bg-orange-600 hover:bg-orange-700', soft: 'bg-orange-50 dark:bg-orange-950/30', ring: 'focus:ring-orange-500 text-orange-600', chipsel: 'bg-orange-500' },
};

const ModuleAccessManagement = ({ title, description, api, accent = 'rose', icon: Icon = ShieldCheckIcon, emailToggle = false }) => {
  const c = ACCENTS[accent] || ACCENTS.rose;
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [emailIds, setEmailIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getModuleAccessUsers();
      const users = res?.data || [];
      setRows(users);
      setSelectedIds(users.filter((u) => u.can_access).map((u) => Number(u.id)));
      setEmailIds(users.filter((u) => u.receive_email && u.can_access && u.email).map((u) => Number(u.id)));
    } catch (e) {
      setError(e.message || 'Kullanıcı yetkileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((u) =>
      [u.full_name, u.email, u.hotel_name, u.department_name, u.role_name]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [rows, query]);

  const isAdminRow = (u) => (u.role_name || '').toLowerCase() === 'admin';
  const sameId = (a, b) => Number(a) === Number(b);
  const hasId = (list, id) => list.some((item) => sameId(item, id));
  const withoutId = (list, id) => list.filter((item) => !sameId(item, id));
  const canEmail = (u) => Boolean(u.email);
  const toggle = (id) => {
    const turningOff = hasId(selectedIds, id);
    setSelectedIds((prev) => (turningOff ? withoutId(prev, id) : [...withoutId(prev, id), Number(id)]));
    if (turningOff) setEmailIds((prev) => withoutId(prev, id));
  };
  const toggleEmail = (id) => {
    const turningOff = hasId(emailIds, id);
    setEmailIds((prev) => (turningOff ? withoutId(prev, id) : [...withoutId(prev, id), Number(id)]));
    if (!turningOff) setSelectedIds((prev) => (hasId(prev, id) ? prev : [...prev, Number(id)]));
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((u) => isAdminRow(u) || hasId(selectedIds, u.id));
  const toggleAllVisible = () => {
    const ids = filtered.filter((u) => !isAdminRow(u)).map((u) => Number(u.id));
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !ids.some((item) => sameId(item, id))));
      setEmailIds((prev) => prev.filter((id) => !ids.some((item) => sameId(item, id))));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev.map(Number), ...ids])]);
  };
  const mailableVisible = filtered.filter((u) => canEmail(u));
  const allVisibleEmailed = mailableVisible.length > 0 && mailableVisible.every((u) => hasId(emailIds, u.id));
  const toggleAllEmail = () => {
    const ids = mailableVisible.map((u) => Number(u.id));
    if (allVisibleEmailed) {
      setEmailIds((prev) => prev.filter((id) => !ids.some((item) => sameId(item, id))));
      return;
    }
    setEmailIds((prev) => [...new Set([...prev.map(Number), ...ids])]);
    const accessIds = filtered.filter((u) => !isAdminRow(u) && u.email).map((u) => Number(u.id));
    setSelectedIds((prev) => [...new Set([...prev.map(Number), ...accessIds])]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setOkMsg(null);
    try {
      const accessToSave = [...new Set(selectedIds.map(Number))];
      const mailToSave = emailToggle
        ? [...new Set(emailIds.map(Number))].filter((id) => accessToSave.includes(id))
        : undefined;
      const res = await api.updateModuleAccessUsers(accessToSave, mailToSave);
      const savedUsers = Array.isArray(res?.data) ? res.data : [];
      const supportsEmail = savedUsers.some((u) => Object.prototype.hasOwnProperty.call(u, 'receive_email'));
      if (emailToggle && savedUsers.length && !supportsEmail) {
        setError('Erişim kaydedildi ama mail seçimi sunucuya yazılamadı. Sunucu güncellemesi tamamlanınca tekrar deneyin.');
      } else if (emailToggle && supportsEmail) {
        const missed = mailToSave.filter((id) => {
          const row = savedUsers.find((u) => Number(u.id) === id);
          return row && !row.receive_email;
        });
        if (missed.length) {
          setError('Bazı mail seçimleri kaydedilemedi. Sayfayı yenileyip tekrar deneyin.');
        } else {
          setOkMsg('Yetkiler kaydedildi.');
        }
      } else {
        setOkMsg('Yetkiler kaydedildi.');
      }
      if (savedUsers.length) {
        setRows(savedUsers);
        setSelectedIds(savedUsers.filter((u) => u.can_access).map((u) => Number(u.id)));
        setEmailIds(savedUsers.filter((u) => u.receive_email && u.can_access && u.email).map((u) => Number(u.id)));
      } else {
        await load();
      }
    } catch (e) {
      setError(e.message || 'Yetki güncellemesi başarısız.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`p-4 md:p-8 mx-auto ${emailToggle ? 'max-w-6xl' : 'max-w-5xl'}`}>
      <div className="flex items-start gap-3 mb-6">
        <div className={`shrink-0 grid place-items-center h-11 w-11 rounded-2xl ${c.soft} ${c.text}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">{error}</div>
      )}
      {okMsg && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300">{okMsg}</div>
      )}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        {/* Araç çubuğu */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 dark:border-gray-700 p-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Kullanıcı, otel, departman ara…"
              className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 pl-9 pr-3 py-2 text-sm text-gray-800 dark:text-gray-100 outline-none focus:border-gray-400"
            />
          </div>
          <button onClick={load} title="Yenile"
            className="inline-flex items-center gap-1 rounded-xl border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            <ArrowPathIcon className="h-4 w-4" /> Yenile
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Seçili: <span className={`font-semibold ${c.text}`}>{selectedIds.length}</span>
            {emailToggle && (
              <span className="ml-2">
                Mail: <span className={`font-semibold ${c.text}`}>{emailIds.length}</span>
              </span>
            )}
          </span>
          <button onClick={handleSave} disabled={saving}
            className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium text-white shadow ${c.bg} disabled:opacity-50`}>
            <CheckIcon className="h-4 w-4" /> {saving ? 'Kaydediliyor…' : 'Yetkileri Kaydet'}
          </button>
        </div>

        {/* Tablo */}
        <div className="max-h-[62vh] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900/80 backdrop-blur text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium w-12">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible}
                    title="Görünenlerin erişimini seç"
                    className={`h-4 w-4 rounded border-gray-300 ${c.ring}`} />
                </th>
                {emailToggle && (
                  <th className="px-4 py-2.5 text-left font-medium w-24">
                    <span className="inline-flex items-center gap-1">
                      <input type="checkbox" checked={allVisibleEmailed} onChange={toggleAllEmail}
                        title="Görünenlerin mailini seç"
                        className={`h-4 w-4 rounded border-gray-300 ${c.ring}`} />
                      <EnvelopeIcon className="h-4 w-4" />
                      Mail
                    </span>
                  </th>
                )}
                <th className="px-4 py-2.5 text-left font-medium">Kullanıcı</th>
                <th className="px-4 py-2.5 text-left font-medium">Otel</th>
                <th className="px-4 py-2.5 text-left font-medium">Departman</th>
                <th className="px-4 py-2.5 text-left font-medium">Rol</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={emailToggle ? 6 : 5} className="px-4 py-10 text-center text-gray-400">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={emailToggle ? 6 : 5} className="px-4 py-10 text-center text-gray-400">Kullanıcı bulunamadı.</td></tr>
              ) : filtered.map((u) => {
                const admin = isAdminRow(u);
                const checked = admin || hasId(selectedIds, u.id);
                const mailed = hasId(emailIds, u.id);
                return (
                  <tr key={u.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/40 ${checked ? c.soft : ''}`}>
                    <td className="px-4 py-2.5">
                      <input type="checkbox" checked={checked} disabled={admin} onChange={() => toggle(u.id)}
                        title={admin ? 'Admin her zaman erişebilir' : 'Modül erişimi'}
                        className={`h-4 w-4 rounded border-gray-300 ${c.ring} disabled:opacity-60`} />
                    </td>
                    {emailToggle && (
                      <td className="px-4 py-2.5">
                        <input type="checkbox" checked={Boolean(u.email) && mailed} disabled={!u.email}
                          onChange={() => toggleEmail(u.id)}
                          title={u.email ? 'Araç hatırlatma maili. İşaretlenince erişim de açılır.' : 'E-posta adresi yok'}
                          className={`h-4 w-4 rounded border-gray-300 ${c.ring} disabled:opacity-40`} />
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{u.full_name || '-'}</div>
                      <div className="text-xs text-gray-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{u.hotel_name || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{u.department_name || '-'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${admin ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {u.role_name || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        Admin rolündeki kullanıcılar her zaman erişebilir; erişim kutusu değiştirilemez.
        {emailToggle ? ' Mail kutusu işaretli kullanıcılara araç hatırlatma e-postası gider ve erişimleri de açılır. E-posta adresi boş olan kullanıcıya mail gitmez.' : ''}
      </p>
    </div>
  );
};

export default ModuleAccessManagement;
